import React, { createContext, useContext, useState } from "react";
import { DEFAULT_SECTIONS_DATA } from "./utils/apis/getSalePageTemplateDataApi/getSalePageTemplateDataApi.constants";
import {
    IColorPaletteFromApi,
    ISalePageSectionsDataFromApi,
} from "./utils/apis/getSalePageTemplateDataApi/getSalePageTemplateDataApi.interfaces";
import { ISalePageSalePageBasicDataFromApi } from "./utils/apis/getSalePageBasicDataApi/getSalePageBasicDataApi.interfaces";
import { MainCtaButtonContextProvider } from "./contexts/MainCtaButtonContext";
import { IRecordedContentCatalogueDataFromApi } from "./utils/apis/getRecordedContentCatalogueDataApi/getRecordedContentCatalogueDataApi.interfaces";
import { useSalePageBuilder } from "./utils/useSalePageBuilder";
import { useLoadAllSectionsOnInteraction } from "./utils/useLoadAllSectionsOnInteraction";
import { ssr_screen_size_defaults } from "./constants";

export type ISalePageContextValue = {
    isPreview: boolean;
    disableAnimation: boolean;
    basicData: ISalePageSalePageBasicDataFromApi;
    colorPalette: IColorPaletteFromApi;
    sectionsData: ISalePageSectionsDataFromApi;
    abTesting?: {
        // Variable to determine the default screen size configuration for server-side rendering.
        ssrScreenSizeDefault?:
            | typeof ssr_screen_size_defaults.mobile
            | typeof ssr_screen_size_defaults.desktop;
    };
};

const initialContextValue: ISalePageContextValue = {
    isPreview: false,
    disableAnimation: false,
    basicData: {} as ISalePageSalePageBasicDataFromApi,
    colorPalette: {},
    sectionsData: JSON.parse(JSON.stringify(DEFAULT_SECTIONS_DATA)),
    abTesting: {},
};

const SalePageContext = createContext(initialContextValue);

/**
 * for those props which we dont want to drill into template component
 */
export function SalePageContextProvider({
    children,
    preview = false,
    disableAnimation = false,
    basicData = initialContextValue.basicData,
    colorPalette = initialContextValue.colorPalette,
    sectionsData = initialContextValue.sectionsData,
    recordedContentCatalogueItemsList,
    abTesting,
}: {
    children: React.ReactNode;
    preview?: boolean;
    disableAnimation?: boolean;
    basicData?: ISalePageSalePageBasicDataFromApi;
    colorPalette?: IColorPaletteFromApi;
    sectionsData?: ISalePageSectionsDataFromApi;
    recordedContentCatalogueItemsList?: IRecordedContentCatalogueDataFromApi;
    abTesting: ISalePageContextValue["abTesting"];
}) {
    const [contextValue, setContextValue] = useState<ISalePageContextValue>({
        isPreview: preview,
        disableAnimation,
        basicData,
        colorPalette,
        sectionsData,
        abTesting,
    });

    useSalePageBuilder({
        contextValue,
        setContextValue,
        recordedContentCatalogueItemsList,
    });

    useLoadAllSectionsOnInteraction({
        salePageUuid: basicData.uuid,
        contextValue,
        setContextValue,
        recordedContentCatalogueItemsList,
    });

    const withCtaButtonContext = (
        <MainCtaButtonContextProvider>{children}</MainCtaButtonContextProvider>
    );

    const withSalePageContext = (
        <SalePageContext.Provider value={contextValue}>
            {withCtaButtonContext}
        </SalePageContext.Provider>
    );

    const finalComponent = withSalePageContext;

    return finalComponent;
}
SalePageContextProvider.defaultProps = {
    sectionsData: undefined,
};

export function useSalePageContext() {
    return useContext(SalePageContext);
}
